import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import Hls from 'hls.js';
import { loadFlowerSdk, parseVastAd, resolvePlayerKind } from '@apm/bloom-receiver-sdk';

import { isAutoplayRejection } from '../auto-start.js';
import { ONAIR_HLS_CONFIG, ONAIR_RETRY_DELAY_MS, recoverFatal } from './hls-recovery.js';
import { buffersUnlocked, playbackBuffers } from '../playback-buffers.js';
import { FLOWER_BUNDLE } from '../create-receiver.js';
import { ONAIR_AD_IDLE, PLAY_CLOCK_START, advancePlayClock, reduceOnairAd } from './cspm.js';
import type { OnairAdEvent, OnairAdState } from './cspm.js';
import { startCspmSession } from './cspm-session.js';
import type { CspmSession, CspmStatus } from './cspm-session.js';

/**
 * ONAIR 채널의 재생 (v28, `docs/57` §8 · 절대 규칙 74).
 *
 * ── ★ 리시버가 없다 ─────────────────────────────────────────
 *
 * 서버가 이미 이어 놓은 것을 **클라이언트의 플레이어가 그대로 받는다.** 그래서
 * `packages/bloom-receiver-sdk` 를 **0줄 연다** — 부르는 것은 `resolvePlayerKind()`
 * 하나이고, 그것은 「hls.js 인가 네이티브인가」의 판정이 두 벌이 되면 **아이폰에서만
 * 갈리기** 때문이다(`docs/29` D6 · v19 가 실기로 값을 치른 자리).
 *
 * ── ★★ `<video>` 를 새로 만들지 않는다 ──────────────────────
 *
 * **WebKit 은 소리 있는 재생 허가를 엘리먼트 단위로 기억한다**(규칙 27 의 예외 ·
 * `playback-buffers.ts`). 새로 만들면 아이폰에서만 `PLAYBACK_FAILED` 이고 노트북에서는
 * 영원히 안 드러난다. 그래서 **앱이 들고 있는 그 한 쌍 중 하나**를 빌려 쓴다.
 *
 * ── 되감기가 없다 ───────────────────────────────────────────
 *
 * 창이 20초라 원리적으로 못 돌아간다(`LIVE_WINDOW_SEGMENTS`). `controls` 도 `tabindex` 도
 * 붙이지 않는다 — 붙는 순간 화살표와 스페이스가 seek·pause 를 일으켜 규칙 31 이 깨진다.
 */
export interface OnairPlayerView {
  /** 재생이 한 번이라도 시작됐는가. **게이트의 조건이 아니다** (아래 `gestureRequired`) */
  started: boolean;
  /**
   * v29. **자동재생이 거부돼 제스처가 필요해졌다** (절대 규칙 72 의 자물쇠 ②).
   * `started === false` 와 다른 사실이다 — 채널 전환도 잠깐 그 값이다.
   */
  gestureRequired: boolean;
  /**
   * v29. **첫 프레임이 붙었는가.** 「방송 중」 배지가 이것으로 뜬다.
   *
   * ★ `started` 로 그리면 **검은 화면 위에 「방송 중」**이 뜬다 — `play()` 가 풀리기
   * 전에 참이 되기 때문이고(그 순서가 게이트 지연을 없앤 자리다), 느린 망에서 실제로
   * 그렇게 보인다. 근거 없는 표시는 틀린 것보다 나쁘다(절대 규칙 46).
   */
  playing: boolean;
  /** 재생 실패. **게이트가 고칠 수 있는 실패는 여기 오지 않는다** (아래 ★) */
  error: string | null;
  muted: boolean;
}

export function useOnairPlayer(options: {
  /** `ChannelSummary.onair.streamUrl`. `null` 이면 아직 방송 전이다 */
  streamUrl: string | null;
  container: HTMLElement | null;
  /** 전환으로 들어왔다 — 게이트 없이 바로 시작한다 */
  autoStart: boolean;
  initialMuted: boolean;
  /**
   * v38 — **CSPM 이면 Flower 를 건다** (`docs/77` §5.2 · 절대 규칙 101). 판정은 `onairAdInsertionFor()` 하나이고
   * 화면이 그 결과를 넘긴다 — 이 훅은 다시 판정하지 않는다. `null` 이면 v28 그대로(원 재생목록 · 브레이크는 슬레이트).
   * **마운트에서 한 번 읽는다** — 채널이 바뀌면 화면이 `key` 로 새로 선다(규칙 27).
   */
  cspm?: { adTagUrl: string; channelId: string; deviceId: string } | null;
}) {
  const { streamUrl, container, autoStart, initialMuted } = options;
  const cspm = options.cspm ?? null;
  const cspmRef = useRef(cspm);
  /** v38 — 광고 화면의 상태. 근거는 Flower 의 리스너와 `AdInfo.vastAdXml` 뿐이다(`cspm.ts`) */
  const [ads, dispatchAd] = useReducer(
    (state: OnairAdState, event: OnairAdEvent) => reduceOnairAd(state, event, parseVastAd),
    ONAIR_AD_IDLE,
  );
  /** v38 — Flower 가 섰는가 · 못 섰으면 그 이유(조용한 강등 금지) */
  const [cspmStatus, setCspmStatus] = useState<CspmStatus | null>(null);
  /** v38 — Flower 의 `onError`(재생은 그대로 · 디버그에만) */
  const [flowerErrors, setFlowerErrors] = useState<readonly string[]>([]);
  const sessionRef = useRef<CspmSession | null>(null);
  /** v38 — 재생 시계(`advancePlayClock`). 남은 시간·스킵 초의 축 — 스킵의 소스 재적재로 `currentTime` 이 뛰어도 잇는다 */
  const playClock = useRef(PLAY_CLOCK_START);
  const readPlayClock = useCallback(() => {
    const el = videoRef.current;
    if (el !== null) playClock.current = advancePlayClock(playClock.current, el.currentTime);
    return playClock.current.accSec;
  }, []);
  const [started, setStarted] = useState(false);
  const [gestureRequired, setGestureRequired] = useState(false);
  /** 첫 프레임이 붙었다. `playing` 이벤트가 그 순간이다 */
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(initialMuted);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    if (container === null || streamUrl === null) return;

    // ★ **앱이 들고 있는 엘리먼트를 빌린다** — 새로 만들지 않는다(위 ★★).
    const [el] = playbackBuffers();
    videoRef.current = el;
    el.playsInline = true;
    el.preload = 'auto';
    el.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;' +
      'object-fit:contain;background:#000;opacity:1;z-index:1;';
    container.appendChild(el);

    const listeners = new AbortController();
    /** 되살리기 예약. **정리에서 끊어야** 파괴된 인스턴스에 `startLoad` 가 가지 않는다 */
    let retryTimer: number | undefined;
    el.addEventListener(
      'error',
      () => {
        setError('PLAYBACK_FAILED');
      },
      { signal: listeners.signal },
    );
    // v29. **그림이 실제로 붙은 순간.** 이 엘리먼트는 채널을 건너 재사용되므로
    // (`playback-buffers.ts`) 마운트마다 거짓으로 되돌린다 — 아래 `setPlaying(false)`.
    el.addEventListener(
      'playing',
      () => {
        setPlaying(true);
      },
      { signal: listeners.signal },
    );
    setPlaying(false);
    // v38 — 재생 시계는 마운트마다 새로 · `timeupdate` 마다 읽어 걸음이 상한을 넘지 않게 한다(`advancePlayClock`)
    playClock.current = PLAY_CLOCK_START;
    el.addEventListener(
      'timeupdate',
      () => {
        readPlayClock();
      },
      { signal: listeners.signal },
    );

    if (resolvePlayerKind() === 'hls') {
      /*
       * **`Hls.isSupported()` 를 여기서 다시 묻지 않는다** — `resolvePlayerKind()` 가
       * 그 판정의 유일한 자리이고(「그 자리를 늘리지 말 것」), 이 분기는 그 답을 쓰는
       * 두 번째 호출부다.
       *
       * `liveSyncDurationCount` 를 건드리지 않는다 — 기본값(3세그먼트 = 6초)이 곧
       * **두 시청자가 같은 지점에서 시작하는 근거**다(`docs/57` §3.3).
       */
      // v38 — 뒤처지면 라이브로 따라붙는다(`ONAIR_HLS_CONFIG` · 시작 지점은 위 주석 그대로 · 기본값을 명시할 뿐이다)
      const hls = new Hls({ ...ONAIR_HLS_CONFIG });
      hlsRef.current = hls;
      /*
       * ★★ **일시적 실패를 끝으로 취급하지 않는다** (2026-09-06 무대 신고 · `docs/60` §11).
       *
       * v28 은 `data.fatal` 이면 곧바로 오류 화면이었다. 그런데 **리시버 어댑터는
       * 처음부터 되살리고 있었다**(`hls-player.ts` 의 `startLoad`·`recoverMediaError`) —
       * 같은 hls.js 를 쓰면서 한쪽만 포기한 것이다.
       *
       * 배포로 서버가 재기동하면 몇 초 동안 `/live.m3u8` 이 닿지 않는다. 그때
       * **리시버 채널은 넘기고 ONAIR 채널만 죽었다.** 판정은 `recoverFatal` 하나다.
       */
      let netTried = 0;
      let mediaTried = 0;
      hls.on(
        Hls.Events.ERROR,
        (_event: unknown, data: { fatal: boolean; type?: string; details?: string }) => {
          /*
           * **치명적인 것만 화면에 올린다** — 라이브는 세그먼트 하나를 놓쳐도 이어진다.
           *
           * ★★ **무엇이 실패했는지 함께 싣는다** (2026-09-06). `PLAYBACK_FAILED` 하나만
           * 남기면 **화면에도 로그에도 이유가 없다** — 그날 무대 신고를 쫓는 데 세 시간이
           * 들었고, 원인(`levelParsingError`: 불연속 번호 불일치)은 hls.js 가 처음부터
           * 말하고 있었다. 그 값이 코드 옆에 있었으면 곧바로 그 줄로 갔다.
           *
           * 화면의 `.notice__code` 가 이것을 그대로 그린다 — **시청자에게는 뜻 없는
           * 문자열이지만 무대에 선 사람에게는 그것이 전부다.**
           */
          if (!data.fatal) return;
          const how = recoverFatal({ type: data.type ?? '', netTried, mediaTried });
          if (how === 'reload') {
            netTried += 1;
            /*
             * ★ **간격을 둔다.** 즉시 던지면 재기동 10초를 못 넘기고 1초 만에 예산을
             * 태운다 — 리시버(즉시 2회)와 갈리는 자리이고, 그래서 판정도 따로 있다.
             */
            retryTimer = window.setTimeout(() => {
              hls.startLoad();
            }, ONAIR_RETRY_DELAY_MS);
            return;
          }
          if (how === 'recoverMedia') {
            mediaTried += 1;
            hls.recoverMediaError();
            return;
          }
          setError(`PLAYBACK_FAILED:${data.details ?? '?'}`);
        },
      );
      hls.attachMedia(el);
      const insertion = cspmRef.current;
      if (insertion === null) {
        hls.loadSource(streamUrl);
      } else {
        /*
         * ★ v38 — **`attachMedia` 뒤에** Flower 가 같은 hls.js 를 감싼다(`docs/77` §5.2 · 셋째 `<video>` 없음).
         * 적재가 끝나야 주소가 정해지므로 `loadSource` 는 세션이 부른다 — 성공이면 바뀐 주소, 실패면 원 주소(D8).
         */
        sessionRef.current = startCspmSession(
          { streamUrl, adTagUrl: insertion.adTagUrl, channelId: insertion.channelId, deviceId: insertion.deviceId },
          {
            load: () => loadFlowerSdk({ src: FLOWER_BUNDLE, deviceId: insertion.deviceId }),
            mountAdView: mountHiddenAdView,
            player: hls,
            loadSource: (url) => {
              hls.loadSource(url);
            },
            mediaSec: readPlayClock,
            dispatch: dispatchAd,
            onStatus: setCspmStatus,
            onFlowerError: (detail) => {
              setFlowerErrors((prev) => [...prev.slice(-9), detail]);
            },
          },
        );
      }
    } else {
      // 아이폰. **네이티브 HLS 가 라이브를 스스로 다룬다** — 이 저장소가 처음 태우는 경로다.
      el.src = streamUrl;
    }

    return () => {
      listeners.abort();
      // **떠나면서 재시도 예약을 끊는다** — 안 끊으면 파괴된 인스턴스에 `startLoad` 가 간다.
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      // v38 — **hls.js 보다 먼저** Flower 를 세운다(그것이 감싼 플레이어가 아직 살아 있을 때 · 스파이크 S7 의 순서)
      sessionRef.current?.stop();
      sessionRef.current = null;
      dispatchAd({ type: 'completed' });
      hlsRef.current?.destroy();
      hlsRef.current = null;
      el.pause();
      el.removeAttribute('src');
      el.load();
      // **DOM 에서 떼지 않는다** — 허가가 엘리먼트에 붙어 있고, 다음 어댑터가
      // `appendChild` 로 자기 자리로 옮겨 간다(`hls-player.ts` 의 `destroy` 와 같은 방침).
      el.muted = true;
    };
  }, [container, streamUrl, readPlayClock]);

  const play = useCallback(() => {
    const el = videoRef.current;
    if (el === null) return;
    el.muted = muted;
    /*
     * ★★ **부를 때 올린다. `play()` 가 풀리기를 기다리지 않는다** (2026-09-05 무대 신고).
     *
     * `useReceiver` 가 `receiver.start()` **직전에** `setStarted(true)` 하는 그 자리와
     * 같은 모양이다. 처음에는 `.then()` 에서 올렸고, 그러면 **매니페스트를 받고 첫
     * 프레임이 붙을 때까지 게이트가 남는다** — 배포된 화면에서 실측 **102·114·123·131ms**
     * 였다(개인화 채널은 같은 조건에서 **1~10ms**). 「채널을 바꿀 때마다 시작 화면이
     * 잠시 보인다」가 그것이고, **느린 망과 폰에서는 더 길다.**
     *
     * 되돌림은 아래 `catch` 가 갖는다 — 그 둘이 절대 규칙 72 의 「자물쇠가 둘인 이유」다:
     * **묻고, 틀렸을 때 되돌린다.**
     */
    setStarted(true);
    setGestureRequired(false);
    void el
      .play()
      .then(() => {
        setError(null);
      })
      .catch((cause: unknown) => {
        /*
         * ★ **거부는 오류가 아니라 게이트다** (절대 규칙 72). 아직 소리 있는 재생이 한
         * 번도 붙지 않은 문서에서 `NotAllowedError` 는 **게이트가 고칠 수 있는 유일한
         * 실패**이고, 스크림으로 덮으면 시청자에게 고장으로 보인다.
         *
         * ★★ **판정을 `buffersUnlocked()` 로 좁힌다** — 규칙 72 가 「넓히면 재생 중의
         * 실패까지 게이트로 떨어져 컨트롤 바가 사라진다」고 적은 그 자리이고,
         * `useReceiver` 의 같은 분기와 **한 글자도 다르지 않아야 한다.**
         */
        if (!buffersUnlocked() && isAutoplayRejection(cause)) {
          setStarted(false);
          // ★ v29. **게이트를 부르는 것은 이 값 하나다** (`showsStartGate`).
          setGestureRequired(true);
          return;
        }
        setError('PLAYBACK_FAILED');
      });
  }, [muted]);

  const toggleMuted = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      if (videoRef.current !== null) videoRef.current.muted = next;
      return next;
    });
  }, []);

  // 전환으로 들어왔으면 게이트 없이 시작한다 — 전환을 부른 것 자체가 제스처다.
  useEffect(() => {
    if (!autoStart || started || container === null || streamUrl === null) return;
    play();
  }, [autoStart, started, container, streamUrl, play]);

  /** v38 — 앱이 그린 스킵 버튼. Flower 가 남은 시간을 다시 묻는다(규칙 101) */
  const skipAd = useCallback(() => sessionRef.current?.skipCurrentAd() ?? false, []);
  /** v38 — 광고 남은 시간·스킵 초의 축. **재생 시계**다(`currentTime` 도 벽시계도 아니다 · `advancePlayClock`) */
  const getMediaSec = readPlayClock;
  /** v38 — 라이브 엣지에서 얼마나 뒤인가(초 · hls.js) — 디버그 통로만 읽는다(브레이크 뒤 지연 증가 · `docs/77` §10.3 ④) */
  const getLatency = useCallback(() => {
    const latency = hlsRef.current?.latency;
    return typeof latency === 'number' && Number.isFinite(latency) ? latency : null;
  }, []);
  /**
   * v38 — 지금 프레임의 `PROGRAM-DATE-TIME`(hls.js `playingDate`)이 벽시계에서 얼마나 뒤인가(초). 디버그 통로만 읽는다.
   * `latency` 는 재생목록 끝(2초 걸음)에 묶여 브레이크마다 ±0.8초 출렁였다(CS8 의 30분 판 둘) — 이것은 프레임 단위다.
   */
  const getPlayingLagSec = useCallback(() => {
    const date = hlsRef.current?.playingDate;
    return date instanceof Date ? (Date.now() - date.getTime()) / 1000 : null;
  }, []);

  const view: OnairPlayerView = { started, gestureRequired, playing, error, muted };
  return { view, play, toggleMuted, ads, cspmStatus, flowerErrors, skipAd, getMediaSec, getLatency, getPlayingLagSec };
}

/**
 * v38 · Flower 의 숨은 광고 뷰 자리 (`docs/77` §5.2).
 *
 * ★ **`display: none` 인 부모 안에 둔다** — 화면 밖(`left:-10000px`)으로만 밀면 Flower 가 그 안에 만드는 버튼
 * (「자세히 보기」·스킵)이 **`getClientRects()` 를 가진 채** 남아 리모컨 포커스 링의 후보가 된다(규칙 29 의 후보 규칙 ·
 * 왼쪽 화살표 끝에서 보이지 않는 버튼으로 간다). 그려지지 않으면 후보가 아니고, Flower 는 자기 버튼이 그려지지 않았으면
 * 누름을 거절한다(`FlowerAdUIViewImpl.ts` 의 `offsetParent === null`) — 앱의 스킵은 `skipCurrentAd()` 라 무관하다.
 */
function mountHiddenAdView(): { container: HTMLElement; remove: () => void } {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.dataset.onairAdView = '';
  host.style.cssText = 'display:none';
  const container = document.createElement('div');
  host.appendChild(container);
  document.body.appendChild(host);
  return {
    container,
    remove: () => {
      host.remove();
    },
  };
}
